import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {initMenuItem} from './user-menu-panel-helper';
import type {MenuItemData, MenuItemTag} from '@PosterWhiteboard/items/menu-item/menu-item.types';
import {PosLoadingStates} from './user-menu-panel.types';

interface InitItemsDataProps {
  unlinkedItemIds: string[];
  isPosAccountLinked: boolean;
  offset: number;
  selectedLayout: string;
  categories: MenuItemTag[];
}

interface POSItemsUpdatedState {
  posLoadingState: PosLoadingStates;
  posCategories?: MenuItemTag[];
  searchingWithTags: boolean;
  posListCursor?: string;
}

interface ResetAllItemsProps {
  unlinkedItemIds: string[];
  selectedLayout: string;
}

interface StateProps {
  isPosSectionActive: boolean;
  inEditMode: boolean;
  activeItem: MenuItemData;
  selectedLayout: string;
  unlinkedItemIds: string[];
  posLoadingState: PosLoadingStates;
  isPosAccountLinked: boolean;
  userItemsLoaded: boolean;
  selectionSectionSynced: boolean;
  offset: number;
  posCategories: MenuItemTag[];
  categories: MenuItemTag[];
  searchingWithTags: boolean;
  loadingToken: string;
  posListCursor?: string;
}

const initialState: StateProps = {
  selectedLayout: 'layout-10',
  isPosSectionActive: false,
  inEditMode: false,
  unlinkedItemIds: [],
  activeItem: initMenuItem(),
  posLoadingState: PosLoadingStates.DEFAULT,
  isPosAccountLinked: false,
  userItemsLoaded: false,
  selectionSectionSynced: true,
  offset: 0,
  posCategories: [],
  categories: [],
  searchingWithTags: false,
  loadingToken: '',
};

const menuSlice = createSlice({
  name: 'menuModal',
  initialState,
  reducers: {
    setPosLoadingState: (state, action: PayloadAction<PosLoadingStates>) => {
      state.posLoadingState = action.payload;
      if (action.payload !== PosLoadingStates.DEFAULT) {
        state.isPosSectionActive = true;
      }
    },
    onLoggedIntoPosAccount: (state) => {
      state.posLoadingState = PosLoadingStates.LOADING_ITEMS;
      state.isPosAccountLinked = true;
    },
    setIsPosSectionActive: (state, action: PayloadAction<boolean>) => {
      state.isPosSectionActive = action.payload;
      if (!action.payload) {
        state.posLoadingState = PosLoadingStates.DEFAULT;
      }
    },
    setSelectedLayout: (state, action: PayloadAction<string>) => {
      state.selectedLayout = action.payload;
    },
    syncSelectedSection: (state, action: PayloadAction<boolean>) => {
      state.selectionSectionSynced = action.payload;
    },
    initItemsData: (state, action: PayloadAction<InitItemsDataProps>) => {
      state.unlinkedItemIds = action.payload.unlinkedItemIds;
      state.userItemsLoaded = true;
      state.isPosAccountLinked = action.payload.isPosAccountLinked;
      state.offset = action.payload.offset;
      state.selectedLayout = action.payload.selectedLayout;
      state.categories = action.payload.categories;
    },
    resetMenuItems: (state, action: PayloadAction<ResetAllItemsProps>) => {
      state.unlinkedItemIds = action.payload.unlinkedItemIds;
      state.inEditMode = false;
      state.isPosSectionActive = false;
      state.activeItem = initMenuItem();
      state.selectedLayout = action.payload.selectedLayout;
      updateAllCategoriesSelection(state);
    },
    toggleEditMode: (state, action: PayloadAction<MenuItemData | null>) => {
      state.inEditMode = !!action.payload;
      state.activeItem = action.payload ? action.payload : initMenuItem();
    },
    posAccountUnlinked: (state) => {
      state.isPosAccountLinked = false;
      state.isPosSectionActive = false;
      state.posLoadingState = PosLoadingStates.DEFAULT;
    },
    setUnlinkedItems: (state, action: PayloadAction<string[]>) => {
      state.unlinkedItemIds = action.payload;
    },
    setItemsOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },
    setUserItemsLoaded: (state, action: PayloadAction<boolean>) => {
      state.userItemsLoaded = action.payload;
    },
    linkItem: (state, action: PayloadAction<string>) => {
      const unlinkedIdsArray = state.unlinkedItemIds;
      const index = unlinkedIdsArray.indexOf(action.payload);
      if (index === -1) {
        return;
      }

      unlinkedIdsArray.splice(index, 1);
      state.unlinkedItemIds = unlinkedIdsArray;
    },
    updatePosListCursor: (state, action: PayloadAction<string | undefined>) => {
      state.posListCursor = action.payload;
    },
    updateCategoriesSelection: (state, action: PayloadAction<string>) => {
      const allCategories = [...state.categories];
      state.categories = allCategories.map((category) => {
        return {
          ...category,
          isSelected: category.id === action.payload ? !category.isSelected : category.isSelected,
        };
      });
    },
    setIsSearchingWithTags: (state, action: PayloadAction<boolean>) => {
      state.searchingWithTags = action.payload;
    },
    updatePosCategories: (state, action: PayloadAction<MenuItemTag[]>) => {
      state.posCategories = action.payload.map((category) => {
        return {
          ...category,
          isSelected: false,
        };
      });
    },
    updatePosCategoriesSelection: (state, action: PayloadAction<string>) => {
      const allCategories = [...state.posCategories];
      state.posCategories = allCategories.map((category) => {
        return {
          ...category,
          isSelected: category.id === action.payload ? !category.isSelected : category.isSelected,
        };
      });
      state.searchingWithTags = true;
    },
    removeAllFilters: (state) => {
      state.categories = state.categories.map((category) => {
        return {
          ...category,
          isSelected: false,
        };
      });
    },
    updateLoadingToken: (state, action: PayloadAction<string>) => {
      state.loadingToken = action.payload;
    },
    onPOSItemsUpdated: (state, action: PayloadAction<POSItemsUpdatedState>) => {
      state.posLoadingState = action.payload.posLoadingState;
      state.posListCursor = action.payload.posListCursor;
      state.searchingWithTags = action.payload.searchingWithTags;
      if (action.payload.posCategories) {
        state.posCategories = action.payload.posCategories;
      }
    },
  },
});

const updateAllCategoriesSelection = (state: StateProps): void => {
  const allCategories: MenuItemTag[] = [...state.categories];
  state.categories = allCategories.map((category) => {
    return {
      ...category,
      isSelected: false,
    };
  });
};

export const {
  setSelectedLayout,
  setIsPosSectionActive,
  syncSelectedSection,
  resetMenuItems,
  initItemsData,
  toggleEditMode,
  posAccountUnlinked,
  onLoggedIntoPosAccount,
  setUnlinkedItems,
  setItemsOffset,
  setUserItemsLoaded,
  linkItem,
  updateLoadingToken,
  updateCategoriesSelection,
  updatePosCategoriesSelection,
  removeAllFilters,
  setPosLoadingState,
  onPOSItemsUpdated,
} = menuSlice.actions;
export const menusReducer = menuSlice.reducer;
